/**
 * Stage metadata for the two OpenADS pipelines.
 *
 * Titles come from `CONTRACT.md` §0 (the Rust log parser matches the same
 * strings, so they must not drift). Short labels, inputs and outputs are mined
 * from `wt_final/GUI_launcher.py::PIPELINE_STAGES`, then corrected against the
 * real output tree of `wt_final/output/sub-02e8eb42` — the launcher table is
 * stale in several places (it writes `Aspects_HPload.csv`; the pipeline really
 * writes `ASPECTS_HPload.csv`, and it omits the `*_lesionload.csv` and
 * `BPM_TYPE1` families entirely).
 *
 * `{id}` in a pattern is the subject id, e.g. `sub-02e8eb42`.
 */

import {
  DWI_STAGES,
  DWI_STAGE_TITLES,
  PWI_STAGES,
  PWI_STAGE_TITLES,
  type DwiStage,
  type Modality,
  type PipelineKind,
  type PwiStage,
  type StageId,
} from "@/ipc/contract";

export interface StageMeta {
  id: StageId;
  modality: Modality;
  /** 1-based position in the pipeline; the execution order is fixed. */
  order: number;
  /** Banner text the runner logs — also the contract's display title. */
  title: string;
  /** Short label for dense tree/table rows. */
  label: string;
  description: string;
  /**
   * Dependencies the pipeline config documents as mandatory
   * (`configs/pwi_pipeline.yaml` header comment). Violating one is an error.
   */
  hardDeps: StageId[];
  /**
   * Dependencies implied by the data flow: the stage reads a file an earlier
   * stage writes. Deselecting one is legal when a previous run already left
   * those files in the output folder, so this is a warning, not an error.
   */
  dataDeps: StageId[];
  inputs: string[];
  outputs: string[];
}

const DWI_META: Record<DwiStage, Omit<StageMeta, "modality" | "order" | "title">> = {
  prepdata: {
    id: "prepdata",
    label: "Prep Data",
    description: "Read the raw DWI/ADC/b0 volumes and write harmonised working copies.",
    hardDeps: [],
    dataDeps: [],
    inputs: ["{id}_DWI.nii.gz", "{id}_ADC.nii.gz (optional)", "{id}_b0.nii.gz (optional)", "{id}_stroke.nii.gz (optional ground truth)"],
    outputs: [
      "DWI/preprocess/{id}_DWI.nii.gz",
      "DWI/preprocess/{id}_ADC.nii.gz",
      "DWI/preprocess/{id}_B0.nii.gz",
    ],
  },
  gen_mask: {
    id: "gen_mask",
    label: "Brain Mask",
    description: "SynthStrip brain mask derived from the preprocessed DWI.",
    hardDeps: [],
    dataDeps: ["prepdata"],
    inputs: ["DWI/preprocess/{id}_DWI.nii.gz"],
    outputs: ["DWI/preprocess/{id}_DWIbrain-mask.nii.gz"],
  },
  skull_strip: {
    id: "skull_strip",
    label: "Skull Strip",
    description: "Apply the brain mask to DWI, ADC and b0.",
    hardDeps: [],
    dataDeps: ["gen_mask"],
    inputs: [
      "DWI/preprocess/{id}_DWI.nii.gz",
      "DWI/preprocess/{id}_ADC.nii.gz",
      "DWI/preprocess/{id}_B0.nii.gz",
      "DWI/preprocess/{id}_DWIbrain-mask.nii.gz",
    ],
    outputs: [
      "DWI/preprocess/{id}_DWI_brain.nii.gz",
      "DWI/preprocess/{id}_ADC_brain.nii.gz",
      "DWI/preprocess/{id}_B0_brain.nii.gz",
    ],
  },
  registration: {
    id: "registration",
    label: "Registration",
    description: "ANTs Affine + SyN from native space to the JHU MNI152 template.",
    hardDeps: [],
    dataDeps: ["skull_strip"],
    inputs: [
      "DWI/preprocess/{id}_DWI_brain.nii.gz",
      "DWI/preprocess/{id}_ADC_brain.nii.gz",
      "DWI/preprocess/{id}_B0_brain.nii.gz",
    ],
    outputs: [
      "DWI/registration/{id}_DWI_space-MNI152_aff.nii.gz",
      "DWI/registration/{id}_ADC_space-MNI152_aff.nii.gz",
      "DWI/registration/{id}_DWI_space-MNI152_affsyn.nii.gz",
      "DWI/registration/{id}_ADC_space-MNI152_affsyn.nii.gz",
      "DWI/registration/{id}_DWIbrain-mask_space-MNI152_aff.nii.gz",
      "DWI/registration/{id}_aff_space-individual2MNI152.mat",
      "DWI/registration/{id}_invaff_space-MNI1522individual.mat",
      "DWI/registration/{id}_warp_space-MNI1522MNI152.nii.gz",
      "DWI/registration/{id}_invwarp_space-MNI1522MNI152.nii.gz",
      "DWI/registration/{id}_registration_manifest.json",
    ],
  },
  inference: {
    id: "inference",
    label: "Lesion Seg",
    description: "DAGMNet_CH3 stroke-core segmentation in MNI space, warped back to native.",
    hardDeps: [],
    dataDeps: ["registration"],
    inputs: [
      "DWI/registration/{id}_DWI_space-MNI152_aff.nii.gz",
      "DWI/registration/{id}_ADC_space-MNI152_aff.nii.gz",
      "DWI/registration/{id}_DWIbrain-mask_space-MNI152_aff.nii.gz",
    ],
    outputs: [
      "DWI/segment/{id}_stroke-mask_space-MNI152.nii.gz",
      "DWI/segment/{id}_stroke-mask_space-MNI152_affsyn.nii.gz",
      "DWI/segment/{id}_stroke-mask.nii.gz",
      "DWI/segment/{id}_metrics.json",
    ],
  },
  report: {
    id: "report",
    label: "Reporting",
    description: "Atlas quantification (QFV and lesion load), radiological report and figures.",
    hardDeps: ["inference"],
    dataDeps: [],
    inputs: [
      "DWI/segment/{id}_stroke-mask_space-MNI152_affsyn.nii.gz",
      "DWI/registration/{id}_ADC_space-MNI152_affsyn.nii.gz",
    ],
    outputs: [
      "DWI/reporting/{id}_VASCULAR_QFV.csv",
      "DWI/reporting/{id}_LOBE_QFV.csv",
      "DWI/reporting/{id}_ASPECTS_QFV.csv",
      "DWI/reporting/{id}_ASPECTSPC_QFV.csv",
      "DWI/reporting/{id}_VENTRICLES_QFV.csv",
      "DWI/reporting/{id}_BPM_TYPE1_QFV.csv",
      "DWI/reporting/{id}_ASPECTS_lesionload.csv",
      "DWI/reporting/{id}_automatic_radiological_report.txt",
      "DWI/reporting/{id}_DWIstroke_space-MNI152.png",
      "DWI/reporting/{id}_DWIstroke.png",
      "DWI/reporting/{id}_ASPECTS_report_interpretation.pdf",
    ],
  },
};

const PWI_META: Record<PwiStage, Omit<StageMeta, "modality" | "order" | "title">> = {
  prepdata: {
    id: "prepdata",
    label: "Prep Data",
    description: "Slice-timing and motion correction, Gd concentration series, PWI brain mask.",
    hardDeps: [],
    dataDeps: [],
    inputs: ["{id}_PWI.nii.gz (4D)", "{id}_PWI.json (sidecar)", "{id}_DWI.nii.gz or {id}_ADC.nii.gz"],
    outputs: [
      "PWI/preprocess/{id}_PWI.nii.gz",
      "PWI/preprocess/{id}_PWI_STC.nii.gz",
      "PWI/preprocess/{id}_PWI_average.nii.gz",
      "PWI/preprocess/{id}_PWIbrain-mask.nii.gz",
      "PWI/preprocess/{id}_PWI_moco_aff/motion_correction_tx_*.mat",
    ],
  },
  gen_mask: {
    id: "gen_mask",
    label: "Brain Mask",
    description: "SynthStrip brain mask on the DWI side, used to drive registration.",
    hardDeps: [],
    dataDeps: ["prepdata"],
    inputs: ["PWI/preprocess/{id}_DWI.nii.gz"],
    outputs: ["PWI/preprocess/{id}_DWIbrain-mask.nii.gz"],
  },
  skull_strip: {
    id: "skull_strip",
    label: "Skull Strip",
    description: "Apply the brain mask to DWI and ADC.",
    hardDeps: [],
    dataDeps: ["gen_mask"],
    inputs: [
      "PWI/preprocess/{id}_DWI.nii.gz",
      "PWI/preprocess/{id}_ADC.nii.gz",
      "PWI/preprocess/{id}_DWIbrain-mask.nii.gz",
    ],
    outputs: ["PWI/preprocess/{id}_DWI_brain.nii.gz", "PWI/preprocess/{id}_ADC_brain.nii.gz"],
  },
  gen_ttp: {
    id: "gen_ttp",
    label: "Generate TTP",
    description: "Time-to-peak map from the Gd concentration series.",
    // configs/pwi_pipeline.yaml: "gen_ttp requires prepdata".
    hardDeps: ["prepdata"],
    dataDeps: [],
    inputs: ["PWI/preprocess/{id}_PWI.nii.gz"],
    outputs: ["PWI/preprocess/{id}_TTP.nii.gz"],
  },
  registration: {
    id: "registration",
    label: "Registration",
    description: "ANTs Affine + SyN of DWI/ADC to MNI; reuses the DWI run's transforms when present.",
    hardDeps: [],
    dataDeps: ["skull_strip"],
    inputs: [
      "PWI/preprocess/{id}_DWI_brain.nii.gz",
      "PWI/preprocess/{id}_ADC_brain.nii.gz",
      "PWI/preprocess/{id}_DWIbrain-mask.nii.gz",
    ],
    outputs: [
      "PWI/registration/{id}_DWI_space-MNI152_aff.nii.gz",
      "PWI/registration/{id}_ADC_space-MNI152_aff.nii.gz",
      "PWI/registration/{id}_DWIbrain-mask_space-MNI152_aff.nii.gz",
      "PWI/registration/{id}_aff_space-individual2MNI152.mat",
      "PWI/registration/{id}_warp_space-MNI1522MNI152.nii.gz",
    ],
  },
  ttpadc_coreg: {
    id: "ttpadc_coreg",
    label: "TTP-ADC Coreg",
    description: "Coregister TTP to ADC, then carry TTP into MNI with the DWI transforms.",
    // configs/pwi_pipeline.yaml: "ttpadc_coreg requires gen_ttp AND registration".
    hardDeps: ["gen_ttp", "registration"],
    dataDeps: [],
    inputs: [
      "PWI/preprocess/{id}_TTP.nii.gz",
      "PWI/preprocess/{id}_PWIbrain-mask.nii.gz",
      "PWI/preprocess/{id}_DWIbrain-mask.nii.gz",
      "PWI/registration/{id}_aff_space-individual2MNI152.mat",
    ],
    outputs: [
      "PWI/registration/{id}_aff_space-individualTTP2ADC.mat",
      "PWI/registration/{id}_invaff_space-ADC2individualTTP.mat",
      "PWI/registration/{id}_TTP_space-DWI.nii.gz",
      "PWI/registration/{id}_TTP_space-DWI_desc-brain.nii.gz",
      "PWI/registration/{id}_TTP_space-MNI152_aff.nii.gz",
      "PWI/registration/{id}_TTP_space-MNI152_affsyn.nii.gz",
      "PWI/registration/{id}_HP_manual_space-MNI152_aff.nii.gz",
    ],
  },
  inference: {
    id: "inference",
    label: "Inference",
    description: "3DUNet_PWI_Ch4 hypoperfusion segmentation from DWI, ADC and TTP in MNI.",
    hardDeps: [],
    dataDeps: ["ttpadc_coreg"],
    inputs: [
      "PWI/registration/{id}_DWI_space-MNI152_aff.nii.gz",
      "PWI/registration/{id}_ADC_space-MNI152_aff.nii.gz",
      "PWI/registration/{id}_TTP_space-MNI152_aff.nii.gz",
    ],
    outputs: [
      "PWI/segmentation/{id}_HP-mask_space-MNI152.nii.gz",
      "PWI/segmentation/{id}_HP-mask_space-MNI152_desc-postproc.nii.gz",
      "PWI/segmentation/{id}_HP-mask_space-MNI152_affsyn.nii.gz",
      "PWI/segmentation/{id}_HP-mask.nii.gz",
      "PWI/segmentation/{id}_HPsymclassic-mask_space-MNI152.nii.gz",
      "PWI/segmentation/{id}_metrics.json",
    ],
  },
  report: {
    id: "report",
    label: "Reporting",
    description: "Hypoperfusion atlas load/QFV tables, radiological report and figures.",
    // configs/pwi_pipeline.yaml: "report requires inference".
    hardDeps: ["inference"],
    dataDeps: [],
    inputs: [
      "PWI/registration/{id}_TTP_space-MNI152_affsyn.nii.gz",
      "PWI/segmentation/{id}_HP-mask_space-MNI152_affsyn.nii.gz",
    ],
    // Verified against the real tree: ASPECTS/ASPECTSPC/BPM_TYPE1 are upper
    // case, Vascular/Lobe are capitalised. GUI_launcher.py has these wrong.
    outputs: [
      "PWI/reporting/{id}_Vascular_HPQFV.csv",
      "PWI/reporting/{id}_Vascular_HPload.csv",
      "PWI/reporting/{id}_Lobe_HPQFV.csv",
      "PWI/reporting/{id}_Lobe_HPload.csv",
      "PWI/reporting/{id}_ASPECTS_HPQFV.csv",
      "PWI/reporting/{id}_ASPECTS_HPload.csv",
      "PWI/reporting/{id}_ASPECTSPC_HPQFV.csv",
      "PWI/reporting/{id}_BPM_TYPE1_HPQFV.csv",
      "PWI/reporting/{id}_automatic_radiological_report.txt",
      "PWI/reporting/{id}_HP_space-MNI152.png",
      "PWI/reporting/{id}_HP.png",
    ],
  },
};

export const DWI_STAGE_META: StageMeta[] = DWI_STAGES.map((id, i) => ({
  ...DWI_META[id],
  modality: "DWI" as const,
  order: i + 1,
  title: DWI_STAGE_TITLES[id],
}));

export const PWI_STAGE_META: StageMeta[] = PWI_STAGES.map((id, i) => ({
  ...PWI_META[id],
  modality: "PWI" as const,
  order: i + 1,
  title: PWI_STAGE_TITLES[id],
}));

export function stagesFor(modality: Modality): StageMeta[] {
  return modality === "DWI" ? DWI_STAGE_META : PWI_STAGE_META;
}

export function stageMeta(modality: Modality, id: StageId): StageMeta | undefined {
  return stagesFor(modality).find((s) => s.id === id);
}

/** A stage is identified by modality + id: `combined` runs both `prepdata`s. */
export function stageKey(modality: Modality, id: StageId): string {
  return `${modality}:${id}`;
}

export function stageTitle(modality: Modality, id: StageId): string {
  return stageMeta(modality, id)?.title ?? id;
}

/** Which modality lists a `combined` run shows; `dwi`/`pwi` show only their own. */
export function modalitiesFor(kind: PipelineKind): Modality[] {
  if (kind === "dwi") return ["DWI"];
  if (kind === "pwi") return ["PWI"];
  return ["DWI", "PWI"];
}

export function fullSelection(kind: PipelineKind): { dwi: DwiStage[]; pwi: PwiStage[] } {
  return {
    dwi: kind === "pwi" ? [] : [...DWI_STAGES],
    pwi: kind === "dwi" ? [] : [...PWI_STAGES],
  };
}

export function emptySelection(): { dwi: DwiStage[]; pwi: PwiStage[] } {
  return { dwi: [], pwi: [] };
}

/** Keeps a selection in pipeline order — `--stages` is order-sensitive. */
export function sortSelection<T extends StageId>(modality: Modality, ids: T[]): T[] {
  const order = stagesFor(modality).map((s) => s.id);
  return [...ids].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

export interface DependencyIssue {
  modality: Modality;
  stage: StageId;
  missing: StageId;
  severity: "error" | "warning";
  message: string;
}

/**
 * Flags selections that break a stage's prerequisites. Hard dependencies (the
 * ones the pipeline config documents) are errors; data-flow dependencies are
 * warnings, because a resumed run can legitimately read the earlier stage's
 * files out of an existing output folder.
 */
export function checkDependencies(modality: Modality, selected: StageId[]): DependencyIssue[] {
  const chosen = new Set(selected);
  const issues: DependencyIssue[] = [];
  for (const meta of stagesFor(modality)) {
    if (!chosen.has(meta.id)) continue;
    for (const dep of meta.hardDeps) {
      if (chosen.has(dep)) continue;
      issues.push({
        modality,
        stage: meta.id,
        missing: dep,
        severity: "error",
        message: `${meta.label} requires ${stageMeta(modality, dep)?.label ?? dep}, which is not selected.`,
      });
    }
    for (const dep of meta.dataDeps) {
      if (chosen.has(dep)) continue;
      issues.push({
        modality,
        stage: meta.id,
        missing: dep,
        severity: "warning",
        message: `${meta.label} reads files written by ${
          stageMeta(modality, dep)?.label ?? dep
        }; they must already exist in the output folder.`,
      });
    }
  }
  return issues;
}

/** Human-readable dependency hint for the stage checkbox rows. */
export function dependencyHint(modality: Modality, id: StageId): string | null {
  const meta = stageMeta(modality, id);
  if (!meta) return null;
  const names = (ids: StageId[]) =>
    ids.map((d) => stageMeta(modality, d)?.label ?? d).join(" + ");
  if (meta.hardDeps.length) return `requires ${names(meta.hardDeps)}`;
  if (meta.dataDeps.length) return `after ${names(meta.dataDeps)}`;
  return null;
}

// --------------------------------------------------------- pipeline facts ---

export interface PipelineFacts {
  stages: number;
  tables: number;
  figures: number;
  reports: number;
  /** Volumes and transforms a run leaves behind, for the ones who want them. */
  volumes: number;
}

/** `.csv` | `.png` | `.pdf`/`.txt` | `.nii.gz`/`.mat`, by extension. */
function kindOf(path: string): keyof PipelineFacts | null {
  const p = path.toLowerCase();
  if (p.endsWith(".csv")) return "tables";
  if (p.endsWith(".png") || p.endsWith(".jpg")) return "figures";
  if (p.endsWith(".pdf") || p.endsWith(".txt")) return "reports";
  if (p.endsWith(".nii.gz") || p.endsWith(".nii") || p.endsWith(".mat")) return "volumes";
  return null;
}

/**
 * What a pipeline writes, counted from the stage table rather than described.
 *
 * The Start cards used to carry a sentence about what each analysis is "for".
 * That reads as a brochure, and a reader deciding which to run wants the size
 * of the thing: how many steps, how much comes out. Both are already written
 * down — `STAGE_META` lists every output of every stage, and it is the same
 * table the Method page and the run view read, so this cannot drift into a
 * claim.
 *
 * `metrics.json` and the like are not counted: a reader asking "what do I get"
 * means the tables, figures and reports, not the bookkeeping.
 */
export function pipelineFacts(kind: PipelineKind): PipelineFacts {
  const facts: PipelineFacts = { stages: 0, tables: 0, figures: 0, reports: 0, volumes: 0 };
  for (const modality of modalitiesFor(kind)) {
    const stages = stagesFor(modality);
    facts.stages += stages.length;
    for (const stage of stages) {
      for (const output of stage.outputs) {
        const k = kindOf(output);
        if (k) facts[k] += 1;
      }
    }
  }
  return facts;
}

/** `6 stages · writes 7 tables, 2 figures and 1 report` */
export function describeFacts(facts: PipelineFacts): string {
  const parts = [
    facts.tables ? `${facts.tables} table${facts.tables === 1 ? "" : "s"}` : null,
    facts.figures ? `${facts.figures} figure${facts.figures === 1 ? "" : "s"}` : null,
    facts.reports ? `${facts.reports} report${facts.reports === 1 ? "" : "s"}` : null,
  ].filter(Boolean) as string[];
  const written =
    parts.length > 1
      ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
      : (parts[0] ?? "no tables or figures");
  return `${facts.stages} stages · writes ${written}`;
}
